import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Loader2, ArrowRightLeft } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { getAccentColor } from "../../../lib/utils/constants";
import { CHART_PALETTE } from "../../../lib/constants/chartPalette";

// ── Country → Continent mapping ──
const CONTINENT_MAP = {
  US: "North America",
  CA: "North America",
  MX: "North America",
  BR: "South America",
  AR: "South America",
  CL: "South America",
  CO: "South America",
  PE: "South America",
  GB: "Europe",
  FR: "Europe",
  DE: "Europe",
  ES: "Europe",
  IT: "Europe",
  NL: "Europe",
  BE: "Europe",
  CH: "Europe",
  AT: "Europe",
  SE: "Europe",
  NO: "Europe",
  DK: "Europe",
  FI: "Europe",
  PL: "Europe",
  PT: "Europe",
  IE: "Europe",
  CZ: "Europe",
  RO: "Europe",
  HU: "Europe",
  GR: "Europe",
  UA: "Europe",
  HR: "Europe",
  BG: "Europe",
  SK: "Europe",
  RU: "Europe",
  TR: "Europe",
  IN: "Asia",
  CN: "Asia",
  JP: "Asia",
  KR: "Asia",
  SG: "Asia",
  TH: "Asia",
  MY: "Asia",
  ID: "Asia",
  PH: "Asia",
  VN: "Asia",
  TW: "Asia",
  HK: "Asia",
  AE: "Middle East",
  SA: "Middle East",
  IL: "Middle East",
  QA: "Middle East",
  AU: "Oceania",
  NZ: "Oceania",
  ZA: "Africa",
  EG: "Africa",
  NG: "Africa",
  KE: "Africa",
  MA: "Africa",
  GH: "Africa",
};

function getContinent(countryCode) {
  if (!countryCode) return "Other";
  const code = countryCode.trim().toUpperCase().slice(0, 2);
  return CONTINENT_MAP[code] || "Other";
}

/**
 * GeoMap — full "Site Locations & Geography" visualization.
 *
 * Renders: Leaflet map · sites table · sites-by-country · chargers-by-country
 * All tables have bidirectional hover with map markers.
 */
export function GeoMap({ siteLocations, locationsLoading, onMoveSite }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerMapRef = useRef({}); // siteId → L.circleMarker
  const [hoveredSite, setHoveredSite] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);

  const ACCENT = getAccentColor();
  const ACCENT_HOVER = CHART_PALETTE.textPrimary;

  // ── Prepare marker data (only sites with resolvable coordinates) ──
  const markers = useMemo(() => {
    if (!Array.isArray(siteLocations) || siteLocations.length === 0) return [];
    const withCoords = siteLocations.filter(
      s => s.lat !== null && s.lat !== undefined && s.lng !== null && s.lng !== undefined
    );
    if (withCoords.length === 0) return [];
    const maxCount = Math.max(...withCoords.map(s => s.chargerCount), 1);
    return withCoords
      .map(s => ({
        key: s.siteId,
        lat: s.lat,
        lng: s.lng,
        value: s.chargerCount,
        radius: 6 + (s.chargerCount / maxCount) * 18,
        displayName: s.siteName,
        subtitle: s.address || "",
        country: s.country || "",
        isApproximate: s.isApproximate || false,
      }))
      .sort((a, b) => b.value - a.value);
  }, [siteLocations]);

  // ── Country aggregations ──
  const { countrySites, countryChargers } = useMemo(() => {
    const sMap = {};
    const cMap = {};
    (siteLocations || []).forEach(s => {
      const c = s.country || "Unknown";
      sMap[c] = (sMap[c] || 0) + 1;
      cMap[c] = (cMap[c] || 0) + s.chargerCount;
    });
    const toSorted = m =>
      Object.entries(m)
        .map(([name, value]) => ({ name, value, continent: getContinent(name) }))
        .sort((a, b) => b.value - a.value);
    return { countrySites: toSorted(sMap), countryChargers: toSorted(cMap) };
  }, [siteLocations]);

  // ── Initialize map ──
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [25, 10],
      zoom: 2,
      minZoom: 2,
      maxZoom: 18,
      scrollWheelZoom: true,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Plot markers & auto-fit bounds ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    Object.values(markerMapRef.current).forEach(m => m.remove());
    markerMapRef.current = {};

    if (markers.length === 0) return;
    const bounds = [];

    markers.forEach(m => {
      const circle = L.circleMarker([m.lat, m.lng], {
        radius: m.radius,
        fillColor: ACCENT,
        fillOpacity: m.isApproximate ? 0.45 : 0.75,
        color: m.isApproximate ? ACCENT : "#fff",
        weight: m.isApproximate ? 1.5 : 2,
        dashArray: m.isApproximate ? "4 3" : undefined,
      }).addTo(map);

      const approxLabel = m.isApproximate
        ? `<br/><span style="font-size:0.65rem;color:#94a3b8;font-style:italic">~ approximate location</span>`
        : "";

      circle.bindTooltip(
        `<div style="text-align:center;line-height:1.5">` +
          `<b>${m.displayName}</b>` +
          (m.subtitle ? `<br/><span style="font-size:0.75rem;color:#6b7280">${m.subtitle}</span>` : "") +
          `<br/><span style="color:${ACCENT};font-weight:700">${m.value}</span> charger${m.value !== 1 ? "s" : ""}` +
          approxLabel +
          `</div>`,
        { direction: "top", offset: [0, -m.radius], className: "geo-map-tooltip" }
      );

      circle.on("mouseover", () => {
        setHoveredSite(m.key);
        setHoveredCountry(null);
      });
      circle.on("mouseout", () => setHoveredSite(null));

      // Store country on the marker for country-level highlighting
      circle._country = m.country;
      markerMapRef.current[m.key] = circle;
      bounds.push([m.lat, m.lng]);
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }, [markers, ACCENT]);

  // ── Highlight by country (bidirectional) ──
  useEffect(() => {
    Object.values(markerMapRef.current).forEach(circle => {
      if (hoveredCountry && circle._country === hoveredCountry) {
        circle.setStyle({ fillColor: ACCENT_HOVER, fillOpacity: 1, weight: 3 });
        circle.openTooltip();
      } else if (!hoveredCountry && !hoveredSite) {
        circle.setStyle({ fillColor: ACCENT, fillOpacity: 0.75, weight: 2 });
        circle.closeTooltip();
      }
    });
  }, [hoveredCountry, hoveredSite, ACCENT]);

  // ── Site-level hover ──
  const highlightSite = useCallback(key => {
    setHoveredSite(key);
    setHoveredCountry(null);
    const circle = markerMapRef.current[key];
    if (circle) {
      circle.setStyle({ fillColor: ACCENT_HOVER, fillOpacity: 1, weight: 3 });
      circle.openTooltip();
    }
  }, []);

  const unhighlightSite = useCallback(
    key => {
      setHoveredSite(null);
      const circle = markerMapRef.current[key];
      if (circle) {
        circle.setStyle({ fillColor: ACCENT, fillOpacity: 0.75, weight: 2 });
        circle.closeTooltip();
      }
    },
    [ACCENT]
  );

  // ── Country-level hover (highlights all markers in that country) ──
  const highlightCountry = useCallback(country => {
    setHoveredCountry(country);
    setHoveredSite(null);
  }, []);

  const unhighlightCountry = useCallback(() => {
    setHoveredCountry(null);
  }, []);

  // ── All sites sorted by charger count (for the table — includes sites without coordinates) ──
  const sortedSites = useMemo(
    () => [...(siteLocations || [])].sort((a, b) => (b.chargerCount || 0) - (a.chargerCount || 0)),
    [siteLocations]
  );

  // ── Compute max values for inline bar widths ──
  const maxSitesByCountry = useMemo(() => Math.max(...countrySites.map(d => d.value), 1), [countrySites]);
  const maxChargersByCountry = useMemo(() => Math.max(...countryChargers.map(d => d.value), 1), [countryChargers]);

  return (
    <div className="geo-section">
      {/* ── Row 1: Map + Sites Table ── */}
      <div className="geo-map-wrapper">
        <div ref={containerRef} className="geo-map-canvas" />

        {locationsLoading && (
          <div className="geo-map-loading">
            <Loader2 size={16} className="geo-map-loading__spinner" /> Loading site locations…
          </div>
        )}

        <div className="geo-map-table-wrap">
          <div className="geo-map-table-scroll">
            <table className="geo-map-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Site ID</th>
                  <th style={{ textAlign: "right" }}>Chargers</th>
                  {onMoveSite && <th style={{ width: 32 }} />}
                </tr>
              </thead>
              <tbody>
                {sortedSites.length > 0
                  ? sortedSites.map(s => {
                      const hasPin = s.lat !== null && s.lat !== undefined && s.lng !== null && s.lng !== undefined;
                      return (
                        <tr
                          key={s.siteId}
                          className={`geo-map-table__row ${
                            hoveredSite === s.siteId || hoveredCountry === s.country ? "geo-map-table__row--active" : ""
                          }`}
                          onMouseEnter={() => hasPin && highlightSite(s.siteId)}
                          onMouseLeave={() => hasPin && unhighlightSite(s.siteId)}
                        >
                          <td>
                            <span className="geo-map-table__name">{s.siteName}</span>
                            <span className="geo-map-table__subtitle">
                              {s.country || ""}
                              {!hasPin ? (s.country ? " · no map pin" : "no map pin") : ""}
                            </span>
                            {s.address && (
                              <span className="geo-map-table__subtitle" style={{ whiteSpace: "normal" }}>
                                {s.address}
                              </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap", color: "#6b7280", fontSize: "0.72rem" }}>{s.siteId}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <span className="geo-map-table__count">{s.chargerCount}</span>
                            {s.onlineCount !== null && s.onlineCount !== undefined && (
                              <span
                                style={{ display: "block", fontSize: "0.65rem", color: "#10b981", fontWeight: 600 }}
                              >
                                {s.onlineCount} online
                              </span>
                            )}
                          </td>
                          {onMoveSite && (
                            <td>
                              <button
                                onClick={() => onMoveSite(s.siteId)}
                                title="Move site to another station"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  color: "#9ca3af",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <ArrowRightLeft size={12} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  : !locationsLoading && (
                      <tr>
                        <td
                          colSpan={onMoveSite ? 4 : 3}
                          style={{ textAlign: "center", color: "#6b7280", padding: "16px" }}
                        >
                          No sites available
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Row 2: Country Distribution (Sites + Chargers side by side) ── */}
      {countrySites.length > 0 && (
        <div className="geo-distribution">
          <div className="geo-distribution__card">
            <h4 className="geo-distribution__title">
              Sites by Country
              <span className="geo-distribution__badge">{countrySites.length} countries</span>
            </h4>
            <div className="geo-distribution__scroll">
              <table className="geo-distribution__table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Continent</th>
                    <th style={{ textAlign: "right" }}>Sites</th>
                    <th style={{ width: "35%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {countrySites.map(d => (
                    <tr
                      key={d.name}
                      className={hoveredCountry === d.name ? "geo-distribution__row--active" : ""}
                      onMouseEnter={() => highlightCountry(d.name)}
                      onMouseLeave={unhighlightCountry}
                    >
                      <td className="geo-distribution__country">{d.name || "Unknown"}</td>
                      <td className="geo-distribution__continent">{d.continent}</td>
                      <td style={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {d.value}
                      </td>
                      <td>
                        <div className="geo-distribution__bar-track">
                          <div
                            className="geo-distribution__bar-fill"
                            style={{ width: `${(d.value / maxSitesByCountry) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="geo-distribution__card">
            <h4 className="geo-distribution__title">
              Chargers by Country
              <span className="geo-distribution__badge">{countryChargers.reduce((s, d) => s + d.value, 0)} total</span>
            </h4>
            <div className="geo-distribution__scroll">
              <table className="geo-distribution__table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Continent</th>
                    <th style={{ textAlign: "right" }}>Chargers</th>
                    <th style={{ width: "35%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {countryChargers.map(d => (
                    <tr
                      key={d.name}
                      className={hoveredCountry === d.name ? "geo-distribution__row--active" : ""}
                      onMouseEnter={() => highlightCountry(d.name)}
                      onMouseLeave={unhighlightCountry}
                    >
                      <td className="geo-distribution__country">{d.name || "Unknown"}</td>
                      <td className="geo-distribution__continent">{d.continent}</td>
                      <td style={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {d.value}
                      </td>
                      <td>
                        <div className="geo-distribution__bar-track">
                          <div
                            className="geo-distribution__bar-fill geo-distribution__bar-fill--chargers"
                            style={{ width: `${(d.value / maxChargersByCountry) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

GeoMap.propTypes = {
  siteLocations: PropTypes.array,
  locationsLoading: PropTypes.bool,
  onMoveSite: PropTypes.func,
};

export default GeoMap;
